/**
 * Merchant API Service
 * Centralized fetch helpers for the provider merchant dashboard.
 * All methods target /api/merchant/* endpoints and include credentials.
 *
 * @module services/api
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const defaultHeaders = {
  'Content-Type': 'application/json',
};

function authHeaders() {
  const token = localStorage.getItem('provider_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      ...defaultHeaders,
      ...authHeaders(),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const json = await res.json();
  return json.data !== undefined ? json.data : json;
}

// ---------------------------------------------------------------------------
// Analytics / Dashboard
// ---------------------------------------------------------------------------

export async function getMerchantDashboard() {
  return request('/merchant/dashboard');
}

export async function getMerchantRevenue(months = 6) {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - months);
  const startDate = start.toISOString().slice(0, 10);
  const endDate = end.toISOString().slice(0, 10);
  return request(`/merchant/analytics?startDate=${startDate}&endDate=${endDate}&granularity=month`);
}

export async function getRecentBookings(limit = 10) {
  return request(`/merchant/bookings?limit=${limit}`);
}

// ---------------------------------------------------------------------------
// Availability / Calendar
// ---------------------------------------------------------------------------

export async function getAvailability() {
  return request('/merchant/availability');
}

export async function updateAvailability(schedule) {
  return request('/merchant/availability', {
    method: 'PUT',
    body: JSON.stringify({ schedule }),
  });
}

export async function getBlockedDates(startDate, endDate) {
  return request(`/merchant/blocked-dates?startDate=${startDate}&endDate=${endDate}`);
}

export async function addBlockedDate(date, reason) {
  return request('/merchant/blocked-dates', {
    method: 'POST',
    body: JSON.stringify({ date, reason: reason || undefined }),
  });
}

export async function removeBlockedDate(id) {
  return request(`/merchant/blocked-dates/${id}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Promotions
// ---------------------------------------------------------------------------

export async function getPromotions(active) {
  const qs = active !== undefined ? `?active=${active}` : '';
  return request(`/merchant/promotions${qs}`);
}

export async function createPromotion(data) {
  return request('/merchant/promotions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePromotion(id, data) {
  return request(`/merchant/promotions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePromotion(id) {
  return request(`/merchant/promotions/${id}/deactivate`, {
    method: 'PUT',
  });
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function getSettings() {
  return request('/merchant/settings');
}

export async function updateSettings(data) {
  return request('/merchant/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
